/*
 * The Step-2 ticket grid (U7 — migrated to Mantine `Table`).
 *
 * An Excel-style selectable table over the live Notion tickets. Columns:
 *   checkbox | Name | Format | Flow | Campaign | Landing page | POV | Headline | Copywriter |
 *   Creative | Link | CTA
 *
 * Campaign / Landing page (2026-10-19) are the ticket's Notion links, in order:
 * a ticket with two of each launches campaign 1 with page 1, campaign 2 with
 * page 2.
 *
 * POV renders the ticket's `Ad Page/POV` multi_select values comma-joined (a
 * ticket can carry several, e.g. `Male, Brand Page`) so bulk selections can be
 * grouped by page identity before the Settings step.
 *
 * Disabling rules (R9):
 *   - `!row.is_valid`      → disabled; reasons shown as inline note (invalid_reasons)
 *   - `row.already_queued` → disabled; reason shown as inline note with a link to
 *                            `/runs/{queued_run_id}` so the operator can inspect the
 *                            blocking run.
 *
 * A row that is BOTH invalid and queued shows the invalid_reasons note (invalid
 * takes priority in the note, queued-reason is separate).
 *
 * The header "select all" toggles only the rows that are VALID AND NOT already_queued.
 *
 * Refresh-overlay: when `isRefreshing` is true the grid wrapper carries
 * `aria-busy` and dims — the caller (Step2) shows the spinner affordances on its
 * buttons. Selection is preserved (the grid renders the same `selected` set
 * unchanged).
 *
 * Per-row refresh indicator: `refreshingIds` is the set of page_ids currently
 * being refreshed — a thin inline `Loader` appears next to the name.
 */

import { Link } from "react-router-dom";
import { Anchor, Checkbox, Loader, NativeSelect, Table, Text } from "@mantine/core";
import type { NotionTicketRow } from "../../api/client";

/** Render a string cell, or a muted dash when empty. */
function cell(value: string | null) {
  return value && value.trim() ? (
    value
  ) : (
    <Text component="span" c="dimmed">
      —
    </Text>
  );
}

export function TicketGrid({
  tickets,
  selected,
  onToggle,
  onToggleAll,
  isRefreshing = false,
  refreshingIds = new Set<string>(),
  copyTabPicks = {},
  onPickCopyTab,
}: {
  tickets: NotionTicketRow[];
  /** The set of selected page ids. */
  selected: Set<string>;
  onToggle: (pageId: string, checked: boolean) => void;
  /** Select / clear all VALID, non-queued rows. */
  onToggleAll: (checked: boolean) => void;
  /** When true, the whole grid is dimmed (Refresh-all in flight). */
  isRefreshing?: boolean;
  /** Page ids whose rows are being individually refreshed. */
  refreshingIds?: Set<string>;
  /** 2026-07-01-002: pageId → chosen copy sub-tab id (absent until the operator picks). */
  copyTabPicks?: Record<string, string>;
  /** Set the chosen copy sub-tab for a ticket whose linked copy tab is empty. */
  onPickCopyTab?: (pageId: string, tabId: string | null) => void;
}) {
  /** A ticket that links an empty copy parent tab and has no pick yet is NOT launchable. */
  const needsPick = (t: NotionTicketRow) => t.needs_copy_tab_pick && !copyTabPicks[t.page_id];
  /** Rows that are selectable: valid, not already queued, and not awaiting a copy-tab pick. */
  const selectableRows = tickets.filter(
    (t) => t.is_valid && !t.already_queued && !needsPick(t),
  );
  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((t) => selected.has(t.page_id));

  if (tickets.length === 0) {
    return (
      <Text c="dimmed" fz="sm">
        No tickets.
      </Text>
    );
  }

  return (
    <Table.ScrollContainer
      minWidth={820}
      aria-busy={isRefreshing || undefined}
      style={
        isRefreshing
          ? { opacity: 0.45, pointerEvents: "none", transition: "opacity 150ms ease-out" }
          : undefined
      }
    >
      <Table highlightOnHover verticalSpacing="xs" striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={44}>
              <Checkbox
                aria-label="Select all valid tickets"
                checked={allSelectableSelected}
                disabled={selectableRows.length === 0}
                onChange={(e) => onToggleAll(e.currentTarget.checked)}
              />
            </Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Format</Table.Th>
            <Table.Th>Flow</Table.Th>
            <Table.Th>Campaign</Table.Th>
            <Table.Th>Landing page</Table.Th>
            <Table.Th>POV</Table.Th>
            <Table.Th>Headline</Table.Th>
            <Table.Th>Copywriter</Table.Th>
            <Table.Th>Creative</Table.Th>
            <Table.Th>Link</Table.Th>
            <Table.Th>CTA</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tickets.map((row) => {
            const isDisabled = !row.is_valid || row.already_queued || needsPick(row);
            return (
              <Table.Tr key={row.page_id}>
                <Table.Td>
                  <Checkbox
                    aria-label={`Select ${row.name ?? row.page_id}`}
                    checked={selected.has(row.page_id)}
                    disabled={isDisabled}
                    onChange={(e) => onToggle(row.page_id, e.currentTarget.checked)}
                  />
                </Table.Td>
                <Table.Td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {cell(row.name)}
                    {refreshingIds.has(row.page_id) && (
                      <Loader size={12} aria-hidden="true" />
                    )}
                  </span>
                  {!row.is_valid && row.invalid_reasons.length > 0 && (
                    <Text role="note" c="red" fz="xs" mt={2} display="block">
                      {row.invalid_reasons.join("; ")}
                    </Text>
                  )}
                  {row.brand_check && !row.brand_check.startsWith("✅") && (
                    <Text role="note" c="orange" fz="xs" mt={2} display="block">
                      {row.brand_check}
                    </Text>
                  )}
                  {row.already_queued && (
                    <Text role="note" c="dimmed" fz="xs" mt={2} display="block">
                      already queued in run{" "}
                      {row.queued_run_id ? (
                        <Anchor component={Link} to={`/runs/${row.queued_run_id}`} fz="xs">
                          {row.queued_run_id}
                        </Anchor>
                      ) : (
                        "another run"
                      )}
                    </Text>
                  )}
                  {/* Batch 5 #5 — Andromeda collision hint: this concept already
                      has a live app-launched instance. Meta groups near-duplicate
                      creatives under one entity and suppresses their delivery, so
                      a relaunch (same page especially) tends to serve at ~zero.
                      Warn-only: the row stays selectable. App-launched ads only. */}
                  {row.already_live && (
                    <Text role="note" c="orange" fz="xs" mt={2} display="block">
                      concept already live
                      {row.already_live.fb_page_id
                        ? ` on page ${row.already_live.fb_page_id}`
                        : ""}
                      {row.already_live.launched_at
                        ? ` since ${new Date(row.already_live.launched_at).toLocaleDateString()}`
                        : ""}
                      {row.already_live.spend !== null
                        ? ` · $${row.already_live.spend} spent (30d)`
                        : ""}{" "}
                      — relaunching risks similarity suppression
                    </Text>
                  )}
                  {/* 2026-07-01-002: the copy doc links an empty parent tab — the
                      operator must pick which sub-tab holds the copy before launch. */}
                  {(row.needs_copy_tab_pick || row.copy_sub_tabs.length > 0) && (
                    <div style={{ marginTop: 4, maxWidth: 260 }}>
                      <NativeSelect
                        size="xs"
                        aria-label={`Copy sub-tab for ${row.name ?? row.page_id}`}
                        value={copyTabPicks[row.page_id] ?? ""}
                        onChange={(e) =>
                          onPickCopyTab?.(row.page_id, e.currentTarget.value || null)
                        }
                        data={[
                          // 2026-10-13: numbered hook sub-tabs on a video ticket need
                          // no pick — each video runs with its own hook's copy unless
                          // the operator chooses one copy for all of them here.
                          row.needs_copy_tab_pick
                            ? { value: "", label: "Pick the copy sub-tab…", disabled: true }
                            : { value: "", label: "Each video gets its own hook" },
                          ...row.copy_sub_tabs.map((s) => ({
                            value: s.tab_id,
                            label: s.title || s.tab_id,
                          })),
                        ]}
                      />
                      {row.needs_copy_tab_pick && !copyTabPicks[row.page_id] && (
                        <Text role="note" c="orange" fz="xs" mt={2} display="block">
                          Copy tab is empty — pick the sub-tab that holds the copy to
                          enable this ticket.
                        </Text>
                      )}
                    </div>
                  )}
                </Table.Td>
                <Table.Td>{cell(row.format)}</Table.Td>
                <Table.Td>{cell(row.flow)}</Table.Td>
                <Table.Td>
                  {cell(
                    (row.campaigns ?? [])
                      .map((c) => c.name ?? "(unknown campaign)")
                      .join(", "),
                  )}
                </Table.Td>
                <Table.Td>
                  {(row.landing_pages ?? []).length === 0
                    ? cell(null)
                    : (row.landing_pages ?? []).map((p) => (
                        <Text key={p.id} fz="sm" title={p.url ?? undefined}>
                          {p.name ?? "(unknown page)"}
                        </Text>
                      ))}
                </Table.Td>
                <Table.Td>{cell(row.ad_page_pov.join(", "))}</Table.Td>
                <Table.Td>{cell(row.headline)}</Table.Td>
                <Table.Td>{cell(row.copywriter)}</Table.Td>
                <Table.Td>{cell(row.creative)}</Table.Td>
                <Table.Td>
                  {row.ticket_url ? (
                    <Anchor href={row.ticket_url} target="_blank" rel="noopener noreferrer">
                      Open
                    </Anchor>
                  ) : (
                    <Text component="span" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{row.cta}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
